import { AlertTriangle, RefreshCw, WifiOff, ShieldAlert, SearchX } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';
import { ApiClientError } from '@/lib/api';
import { Button } from './button';

export interface ErrorStateProps {
  title?: string;
  /** The thrown value, whatever it was. Narrowing happens here, once. */
  error: unknown;
  onRetry?: () => void;
  /** Extra content below the message — a "go back" link, say. */
  children?: ReactNode;
  className?: string;
  /** Fill the viewport. For a whole-page failure rather than one panel. */
  full?: boolean;
}

interface Presentation {
  icon: LucideIcon;
  title: string;
  message: string;
  /** Whether retrying is plausibly useful. */
  retryable: boolean;
}

/**
 * Turn an unknown thrown value into something a person can act on.
 *
 * Deliberately does NOT print a stack or a raw code at the user. It does print
 * the server's own `message`, because the API writes those for humans (a PRN
 * cap being hit, for instance, names the cap and when the oldest dose falls
 * off) and swallowing it would hide the only useful sentence.
 */
export function describeError(error: unknown): Presentation {
  if (error instanceof ApiClientError) {
    switch (error.code) {
      case 'network_error':
        return {
          icon: WifiOff,
          title: 'Cannot reach the server',
          message:
            'The app could not connect. Check the device is on the same network, then try again.',
          retryable: true,
        };
      case 'unauthorized':
        return {
          icon: ShieldAlert,
          title: 'Signed out',
          message: 'Your session has ended. Sign in again to carry on.',
          retryable: false,
        };
      case 'forbidden':
        return {
          icon: ShieldAlert,
          title: 'Not allowed',
          message: error.message,
          retryable: false,
        };
      case 'not_found':
        return {
          icon: SearchX,
          title: 'Not found',
          message: error.message,
          retryable: false,
        };
      case 'rate_limited':
        return {
          icon: AlertTriangle,
          title: 'Too many attempts',
          message: error.message,
          retryable: true,
        };
      case 'validation_failed':
      case 'conflict':
      case 'bad_request':
        return {
          icon: AlertTriangle,
          title: 'That did not work',
          message: error.message,
          retryable: false,
        };
      default:
        return {
          icon: AlertTriangle,
          title: 'Something went wrong',
          message: error.message,
          retryable: true,
        };
    }
  }

  if (error instanceof Error) {
    return {
      icon: AlertTriangle,
      title: 'Something went wrong',
      message: error.message || 'An unexpected error occurred.',
      retryable: true,
    };
  }

  return {
    icon: AlertTriangle,
    title: 'Something went wrong',
    message: 'An unexpected error occurred.',
    retryable: true,
  };
}

/**
 * "The request failed." Never substitute an <EmptyState> for this.
 *
 * CONTRACT §7: an error on a read path has to be visible, because the
 * alternative — a blank list — is indistinguishable from "you have no
 * medication today" and is the most dangerous thing this UI can say.
 */
export function ErrorState({
  title,
  error,
  onRetry,
  children,
  className,
  full = false,
}: ErrorStateProps): JSX.Element {
  const presentation = describeError(error);
  const Icon = presentation.icon;

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-card border border-line bg-surface',
        'px-6 py-10 text-center',
        full ? 'min-h-[60dvh]' : undefined,
        className,
      )}
    >
      <span className="flex size-12 items-center justify-center rounded-full bg-status-missed/10 text-status-missed">
        <Icon className="size-6" aria-hidden="true" />
      </span>
      <div className="space-y-1">
        <p className="text-base font-medium text-ink">{title ?? presentation.title}</p>
        <p className="mx-auto max-w-sm text-sm leading-6 text-muted">
          {presentation.message}
        </p>
      </div>
      {onRetry ? (
        <Button variant="secondary" size="md" onClick={onRetry} className="mt-1">
          <RefreshCw aria-hidden="true" />
          Try again
        </Button>
      ) : null}
      {children}
    </div>
  );
}

/** Compact inline variant for a failed panel inside an otherwise working page. */
export function InlineError({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}): JSX.Element {
  const presentation = describeError(error);
  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-2.5 rounded-lg border border-status-missed/30 bg-status-missed/10',
        'px-3 py-2.5 text-sm text-ink',
        className,
      )}
    >
      <AlertTriangle
        className="mt-0.5 size-4 shrink-0 text-status-missed"
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1">{presentation.message}</span>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className={cn(
            'shrink-0 rounded px-1 font-medium text-status-missed underline underline-offset-2',
            'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
          )}
        >
          Retry
        </button>
      ) : null}
    </div>
  );
}
